import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import config from '../config';

const SECFilingAnalysisModal = ({ selectedFilings, onClose }) => {
  const [prompts, setPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [customSystemPrompt, setCustomSystemPrompt] = useState('');
  const [customUserPrompt, setCustomUserPrompt] = useState('');
  const [useCustomPrompt, setUseCustomPrompt] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [jobStarted, setJobStarted] = useState(false);
  
  // Ref to track if polling should be cancelled
  const cancelledRef = useRef(false);

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      const response = await axios.get(`${config.api.bedrockServiceUrl}/bedrock/prompts?lightweight=true`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Filter prompts that start with "SEC_"
      const secPrompts = (response.data.prompts || []).filter(
        p => p.app_id && p.app_id.startsWith('SEC_')
      );
      setPrompts(secPrompts);
    } catch (err) {
      console.error('Error fetching prompts:', err);
      setError('Failed to load prompts');
    }
  };

  const handleAnalyze = async () => {
    if (!useCustomPrompt && !selectedPrompt) {
      setError('Please select a prompt or use custom prompt');
      return;
    }

    if (useCustomPrompt && !customUserPrompt.trim()) {
      setError('Please enter a custom prompt');
      return;
    }

    setAnalyzing(true);
    setError('');
    setResults([]);
    cancelledRef.current = false;
    setJobStarted(false);

    try {
      // Analyze all filings together in a single request
      const s3Keys = selectedFilings.map(filing => filing.s3_key);
      
      const requestBody = {
        s3_bucket: selectedFilings[0].s3_bucket || 'hff-sec-filings',
        s3_keys: s3Keys,
        file_id: `combined_${selectedFilings[0].filing_id}`,
        original_filename: `${selectedFilings.length}_filings_combined.txt`,
        client_name: [...new Set(selectedFilings.map(f => f.company_name))].join('|'),
        category: 'SEC Filings Analysis'
      };

      if (useCustomPrompt) {
        if (customSystemPrompt.trim()) {
          requestBody.system_prompt = customSystemPrompt;
        }
        requestBody.user_message = customUserPrompt;
      } else {
        requestBody.prompt_name = selectedPrompt;
      }

      // Call the public bedrock endpoint (async processing with job polling)
      const response = await axios.post(
        `${config.api.bedrockServiceUrl}/bedrock/analyze/document/s3`,
        requestBody,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      // Poll for job completion
      const jobId = response.data.job_id;
      setJobStarted(true);
      
      // Check if user cancelled before starting to poll
      if (cancelledRef.current) {
        return;
      }
      
      const result = await pollJobStatus(jobId);
      
      // Only update state if not cancelled
      if (!cancelledRef.current) {
        setResults([result]);
      }
    } catch (err) {
      console.error('Error analyzing filings:', err);
      setError(err.response?.data?.detail || err.message || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const pollJobStatus = async (jobId, maxAttempts = 60) => {
    // Wait a bit before first poll to let the job get created
    await new Promise(resolve => setTimeout(resolve, 3000)); // Initial 3 second wait
    
    for (let i = 0; i < maxAttempts; i++) {
      // Check if polling was cancelled
      if (cancelledRef.current) {
        return { cancelled: true };
      }
      
      try {
        const response = await axios.get(
          `${config.api.bedrockServiceUrl}/bedrock/analyze/document/s3/${jobId}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        const status = response.data.status;

        if (status === 'completed') {
          return {
            success: true,
            answer: response.data.summary || '',
            metadata: response.data
          };
        } else if (status === 'failed') {
          return {
            success: false,
            error: response.data.error || 'Analysis failed'
          };
        }
        // Continue polling if status is 'pending' or 'processing'
      } catch (err) {
        console.error(`Error polling job ${jobId}:`, err);
        // If it's a 404, the job might not be created yet, continue polling
        if (err.response?.status === 404 && i < maxAttempts - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        }
        return {
          success: false,
          error: 'Failed to check job status'
        };
      }
      
      // Wait between polls
      await new Promise(resolve => setTimeout(resolve, 2000));
    }

    return {
      success: false,
      error: 'Analysis timeout - job took too long to complete'
    };
  };

  const handleCopyToClipboard = async () => {
    if (results.length > 0 && results[0].success) {
      try {
        await navigator.clipboard.writeText(results[0].answer);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy to clipboard:', err);
        setError('Failed to copy to clipboard');
      }
    }
  };

  const handleCloseWhileAnalyzing = () => {
    cancelledRef.current = true;
    onClose();
  };

  const handleExportToPDF = async () => {
    if (results.length > 0 && results[0].success) {
      setExporting(true);
      try {
        // Create a temporary div with the markdown content
        const printWindow = window.open('', '', 'width=800,height=600');
        if (!printWindow) {
          throw new Error('Could not open print window');
        }

        // Add content and styling
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>SEC Filing Analysis</title>
              <style>
                body {
                  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                  padding: 40px;
                  max-width: 800px;
                  margin: 0 auto;
                  line-height: 1.6;
                }
                h1, h2, h3, h4, h5, h6 {
                  margin-top: 24px;
                  margin-bottom: 16px;
                  font-weight: 600;
                }
                h1 { font-size: 2em; border-bottom: 1px solid #eee; padding-bottom: 10px; }
                h2 { font-size: 1.5em; }
                h3 { font-size: 1.25em; }
                p { margin-bottom: 16px; }
                ul, ol { margin-bottom: 16px; padding-left: 30px; }
                li { margin-bottom: 8px; }
                code {
                  background-color: #f6f8fa;
                  padding: 2px 6px;
                  border-radius: 3px;
                  font-family: 'Courier New', monospace;
                  font-size: 0.9em;
                }
                pre {
                  background-color: #f6f8fa;
                  padding: 16px;
                  border-radius: 6px;
                  overflow-x: auto;
                  margin-bottom: 16px;
                }
                pre code {
                  background-color: transparent;
                  padding: 0;
                }
                table {
                  border-collapse: collapse;
                  width: 100%;
                  margin-bottom: 16px;
                }
                th, td {
                  border: 1px solid #ddd;
                  padding: 8px;
                  text-align: left;
                }
                th {
                  background-color: #f6f8fa;
                  font-weight: 600;
                }
                blockquote {
                  border-left: 4px solid #ddd;
                  padding-left: 16px;
                  color: #666;
                  margin: 16px 0;
                }
                .header {
                  margin-bottom: 30px;
                  padding-bottom: 20px;
                  border-bottom: 2px solid #eee;
                }
                .filing-info {
                  color: #666;
                  font-size: 0.9em;
                  margin-bottom: 10px;
                }
                @media print {
                  body { padding: 20px; }
                }
              </style>
            </head>
            <body>
              <div class="header">
                <h1>SEC Filing Analysis</h1>
                <div class="filing-info">
                  <strong>Companies:</strong> ${selectedFilings.map(f => f.company_name).join(', ')}<br>
                  <strong>Form Types:</strong> ${[...new Set(selectedFilings.map(f => f.form_type))].join(', ')}<br>
                  <strong>Filing Dates:</strong> ${[...new Set(selectedFilings.map(f => f.filing_date))].join(', ')}<br>
                  <strong>Generated:</strong> ${new Date().toLocaleString()}
                </div>
              </div>
              <div class="content">
                ${results[0].answer.split('\n').map(line => {
                  // Basic markdown to HTML conversion
                  if (line.startsWith('# ')) return `<h1>${line.substring(2)}</h1>`;
                  if (line.startsWith('## ')) return `<h2>${line.substring(3)}</h2>`;
                  if (line.startsWith('### ')) return `<h3>${line.substring(4)}</h3>`;
                  if (line.startsWith('#### ')) return `<h4>${line.substring(5)}</h4>`;
                  if (line.startsWith('- ')) return `<li>${line.substring(2)}</li>`;
                  if (line.trim() === '') return '<br>';
                  // Bold
                  line = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
                  // Italic
                  line = line.replace(/\*(.+?)\*/g, '<em>$1</em>');
                  return `<p>${line}</p>`;
                }).join('\n')}
              </div>
            </body>
          </html>
        `);
        
        printWindow.document.close();
        
        // Wait for content to load then print
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
        
      } catch (err) {
        console.error('Failed to export to PDF:', err);
        setError('Failed to export to PDF. Please try again.');
      } finally {
        setExporting(false);
      }
    }
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              Analyze {selectedFilings.length} Filing{selectedFilings.length !== 1 ? 's' : ''}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={analyzing ? handleCloseWhileAnalyzing : onClose}
            ></button>
          </div>
          
          <div className="modal-body">
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {/* Selected Filings */}
            <div className="mb-3">
              <h6>Selected Filings:</h6>
              <div className="list-group">
                {selectedFilings.map(filing => (
                  <div key={filing.filing_id} className="list-group-item">
                    <div className="d-flex justify-content-between">
                      <div>
                        <strong>{filing.company_name}</strong>
                        <span className="badge bg-info text-dark ms-2">{filing.form_type}</span>
                      </div>
                      <small className="text-muted">{filing.filing_date}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Prompt Selection */}
            {!analyzing && results.length === 0 && (
              <>
                <div className="mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="useCustomPrompt"
                      checked={useCustomPrompt}
                      onChange={(e) => setUseCustomPrompt(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="useCustomPrompt">
                      Use custom prompt instead of saved prompt
                    </label>
                  </div>
                </div>

                {useCustomPrompt ? (
                  <>
                    <div className="mb-3">
                      <label htmlFor="customSystemPrompt" className="form-label">
                        System Prompt (Optional)
                      </label>
                      <textarea
                        className="form-control"
                        id="customSystemPrompt"
                        rows="3"
                        value={customSystemPrompt}
                        onChange={(e) => setCustomSystemPrompt(e.target.value)}
                        placeholder="Enter system prompt..."
                      />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="customUserPrompt" className="form-label">
                        User Prompt <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        id="customUserPrompt"
                        rows="4"
                        value={customUserPrompt}
                        onChange={(e) => setCustomUserPrompt(e.target.value)}
                        placeholder="Enter your analysis prompt..."
                      />
                    </div>
                  </>
                ) : (
                  <div className="mb-3">
                    <label htmlFor="promptSelect" className="form-label">
                      Select Prompt <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      id="promptSelect"
                      value={selectedPrompt}
                      onChange={(e) => setSelectedPrompt(e.target.value)}
                    >
                      <option value="">Choose a prompt...</option>
                      {prompts.map(prompt => (
                        <option key={prompt.prompt_id} value={prompt.app_id}>
                          {prompt.app_id} - {prompt.description || 'No description'}
                        </option>
                      ))}
                    </select>
                    {prompts.length === 0 && (
                      <small className="form-text text-muted">
                        No SEC prompts found. Create prompts with names starting with "SEC_" in Prompt Manager.
                      </small>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Analysis Progress */}
            {analyzing && (
              <div className="text-center py-4">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Analyzing...</span>
                </div>
                <p>Analyzing filings... This may take a few moments.</p>
                <small className="text-muted">Processing {selectedFilings.length} filing{selectedFilings.length !== 1 ? 's' : ''}</small>
                {jobStarted && (
                  <div className="mt-3">
                    <small className="text-info">
                      <i className="bi bi-info-circle me-1"></i>
                      You can close this dialog - the analysis will continue in the background.
                      <br />Check the Analysis tab to view the results when complete.
                    </small>
                  </div>
                )}
              </div>
            )}

            {/* Results */}
            {results.length > 0 && (
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Analysis Results:</h6>
                  {results[0].success && (
                    <div className="btn-group">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleCopyToClipboard}
                        title="Copy to clipboard"
                      >
                        <i className={`bi ${copied ? 'bi-check2' : 'bi-clipboard'} me-1`}></i>
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleExportToPDF}
                        disabled={exporting}
                        title="Export to PDF"
                      >
                        {exporting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            Exporting...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-file-pdf me-1"></i>
                            Export PDF
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
                {results[0].success ? (
                  <div className="border rounded p-3 bg-light markdown-content">
                    <ReactMarkdown>
                      {results[0].answer}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <div className="alert alert-danger">
                    <strong>Error:</strong> {results[0].error}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            {results.length === 0 ? (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={analyzing ? handleCloseWhileAnalyzing : onClose}
                >
                  {analyzing && jobStarted ? 'Close (Continue in Background)' : 'Cancel'}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAnalyze}
                  disabled={analyzing || (!useCustomPrompt && !selectedPrompt) || (useCustomPrompt && !customUserPrompt.trim())}
                >
                  {analyzing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Analyzing...
                    </>
                  ) : (
                    'Analyze Filings'
                  )}
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                onClick={onClose}
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SECFilingAnalysisModal;
